import { Schemas } from '@api-client/generated/types';
import { VAT_RATE } from '@entities/billing/vat';

export type CreditNoteMode = 'global' | 'jobs' | 'custom';

export type InvoiceItem = Schemas.AdminOneTimeInvoiceDto;

export type CustomLine = {
  name?: string;
  description?: string;
  unitPrice?: number;
  totalPrice?: number;
  taxRate?: number;
};

type CreditNoteCapabilities = Pick<
  Schemas.AdminCreditNoteCapabilitiesDto,
  'contractVersion' | 'modes' | 'sourceItemIndexes'
>;

export type CreditNoteChild = {
  documentMetadata?: {
    creditNoteMode?: 'global' | 'source_items' | 'custom' | null;
    creditNoteSourceItemIndexes?: number[] | null;
  } | null;
};

export type CreditSourceHistory = {
  usedIndexes: Set<number>;
  hasUnknownSourceHistory: boolean;
};

export const sumTotal = (items: ({ totalPrice?: number | null } | undefined)[]): number =>
  items.reduce((acc, item) => acc + (Number(item?.totalPrice) || 0), 0);

const normalizeSourceItemIndexes = (
  indexes: number[],
  parentItemCount: number
) =>
  indexes.filter(
    (index, position, allIndexes) =>
      Number.isInteger(index) &&
      index >= 0 &&
      index < parentItemCount &&
      allIndexes.indexOf(index) === position
  );

const hasEnteredValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

const isBlankCustomLine = (line: CustomLine) =>
  !line?.name?.trim() &&
  !line?.description?.trim() &&
  !hasEnteredValue(line?.unitPrice) &&
  !hasEnteredValue(line?.totalPrice);

const getEnteredCustomLines = (customLines: CustomLine[]) =>
  customLines.filter((line) => !isBlankCustomLine(line));

const isValidCustomLine = (line: CustomLine) => {
  const unitPrice = Number(line?.unitPrice);
  const totalPrice = Number(line?.totalPrice);

  return Boolean(
    line?.name?.trim() &&
      Number.isFinite(unitPrice) &&
      unitPrice > 0 &&
      Number.isFinite(totalPrice) &&
      totalPrice > 0
  );
};

export const hasValidCustomLines = (customLines: CustomLine[]) => {
  const enteredLines = getEnteredCustomLines(customLines);

  return enteredLines.length > 0 && enteredLines.every(isValidCustomLine);
};

const normalizeTaxRate = (taxRate: number | undefined) => {
  const rate = Number(taxRate);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : VAT_RATE;
};

const normalizeCustomLines = (customLines: CustomLine[]) => {
  const enteredLines = getEnteredCustomLines(customLines);
  if (!enteredLines.length || !enteredLines.every(isValidCustomLine)) return [];

  return enteredLines.map((line) => ({
    name: line.name!.trim(),
    description: line.description ?? null,
    unitPrice: Number(line.unitPrice),
    quantity: 1,
    totalPrice: Number(line.totalPrice),
    // Preserve the rate the operator applied (the total was computed from it);
    // fall back to the standard rate only for legacy lines without one.
    taxRate: normalizeTaxRate(line.taxRate),
  }));
};

/** Build the versioned payload without mixing mutually exclusive mode fields. */
export const buildCreditNoteRequest = (params: {
  mode: CreditNoteMode;
  parentItemCount: number;
  selectedJobs: number[];
  customLines: CustomLine[];
}): Pick<
  Schemas.AdminCreateCreditNoteDto,
  'mode' | 'sourceItemIndexes' | 'items'
> => {
  const { mode, parentItemCount, selectedJobs, customLines } = params;

  if (mode === 'global') {
    return { mode: 'global' };
  }

  if (mode === 'jobs') {
    const sourceItemIndexes = normalizeSourceItemIndexes(
      selectedJobs,
      parentItemCount
    );

    return { mode: 'source_items', sourceItemIndexes };
  }

  return {
    mode: 'custom',
    items: normalizeCustomLines(customLines),
  };
};

export const hasCreditNoteContractV3 = (
  capabilities: CreditNoteCapabilities | null | undefined
): boolean => {
  const modes = new Set(capabilities?.modes ?? []);

  return Boolean(
    capabilities?.contractVersion === 3 &&
      capabilities.sourceItemIndexes === true &&
      modes.has('global') &&
      modes.has('source_items') &&
      modes.has('custom')
  );
};

/**
 * Replay the server's source-index provenance rules. Any legacy/malformed
 * non-custom child makes source-based modes unavailable instead of guessing.
 */
export const getCreditSourceHistory = (
  children: CreditNoteChild[] | null | undefined,
  parentItemCount: number
): CreditSourceHistory => {
  const usedIndexes = new Set<number>();
  let hasUnknownSourceHistory = false;

  for (const child of children ?? []) {
    const mode = child.documentMetadata?.creditNoteMode;
    if (mode === 'custom') continue;

    const indexes = child.documentMetadata?.creditNoteSourceItemIndexes;
    const validIndexes =
      (mode === 'global' || mode === 'source_items') &&
      Array.isArray(indexes) &&
      indexes.length > 0 &&
      new Set(indexes).size === indexes.length &&
      indexes.every(
        (index) =>
          Number.isInteger(index) && index >= 0 && index < parentItemCount
      );

    if (!validIndexes) {
      hasUnknownSourceHistory = true;
      continue;
    }

    indexes.forEach((index) => usedIndexes.add(index));
  }

  return { usedIndexes, hasUnknownSourceHistory };
};

/** Total value shown to the operator before creating the credit note. */
export const creditNoteTotal = (params: {
  mode: CreditNoteMode;
  parentItems: InvoiceItem[];
  selectedJobs: number[];
  customLines: CustomLine[];
}): number => {
  const { mode, parentItems, selectedJobs, customLines } = params;

  if (mode === 'global') return sumTotal(parentItems);
  if (mode === 'jobs') {
    return sumTotal(
      normalizeSourceItemIndexes(selectedJobs, parentItems.length).map(
        (index) => parentItems[index]
      )
    );
  }

  return sumTotal(getEnteredCustomLines(customLines));
};
